'use client';

import { c as _c } from "react/compiler-runtime";
import { CmsPageTakeover, CmsPageTakeoverImage } from '@spa/top-level-components/cms-page/CmsComponentPage';
import { CmsAiMarkerPosition } from '@spa/generated/graphql/graphql';
import { ImageAiMarkerComponent } from '@spa/components/image-ai-marker/image-ai-marker.component';
import ImageComponent from '@spa/components/image-component/image.component';
import LinkComponent from '@spa/components/link/link.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { ReactElement } from 'react';
import classNames from 'clsx';
import configEnv from '@spa/lib/image-loader/imageConfigEnv';
import { preload } from 'react-dom';
import styles from './takeover-top-banner.component.module.scss';
import useImagesLoader from '@spa/lib/react/hooks/useImagesLoader';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export interface Props {
  readonly takeover: CmsPageTakeover;
  readonly onDismiss?: () => void;
}
const allSizes = configEnv.imageSizes.concat(configEnv.deviceSizes).sort((a, b) => a - b);
const bannerSizes = '(min-width: 1488px) 1488px, 100vw';

// The right-hand corners share space with the advertisement label (bottom) and the
// close button (top), so the marker is inset further there to keep both readable.
const getAiMarkerClassName = (position: CmsAiMarkerPosition | undefined, showCloseButton: boolean): string | undefined => {
  let className: string | undefined;
  if (position === CmsAiMarkerPosition.BOTTOM_RIGHT) {
    className = styles['banner__ai-marker--above-label'];
  } else if (position === CmsAiMarkerPosition.TOP_RIGHT && showCloseButton) {
    className = styles['banner__ai-marker--beside-close'];
  }
  return className;
};
function getClosestConfiguredWidth(width: number): number {
  return allSizes.find(imageSize => imageSize >= width) ?? width;
}
function buildSrcSet(image: CmsPageTakeoverImage, imagesLoader: ReturnType<typeof useImagesLoader>): string {
  const maxWidth = getClosestConfiguredWidth(image.width);
  return allSizes.filter(size => size <= maxWidth).map(size => `${imagesLoader({
    src: image.imgSrc,
    width: size,
    quality: 75
  })} ${size}w`).join(', ');
}
export default function TakeoverTopBannerComponent(t0) {
  const $ = _c(70);
  const {
    takeover,
    onDismiss
  } = t0;
  const imagesLoader = useImagesLoader();
  const {
    formatMessage
  } = useNamespacedIntl("common");
  const {
    formatMessage: formatAiMarkerMessage
  } = useNamespacedIntl("image-ai-marker");
  const isLinked = takeover.link !== null;
  let desktopSrcSet;
  let mobileSrcSet;
  let tabletSrcSet;
  if ($[0] !== imagesLoader || $[1] !== takeover.topBannerDesktop || $[2] !== takeover.topBannerMobile || $[3] !== takeover.topBannerTablet) {
    desktopSrcSet = buildSrcSet(takeover.topBannerDesktop, imagesLoader);
    tabletSrcSet = buildSrcSet(takeover.topBannerTablet, imagesLoader);
    mobileSrcSet = buildSrcSet(takeover.topBannerMobile, imagesLoader);
    preload(imagesLoader({
      src: takeover.topBannerDesktop.imgSrc,
      width: takeover.topBannerDesktop.width,
      quality: 75
    }), {
      as: "image",
      media: "(min-width: 1024px)",
      imageSrcSet: desktopSrcSet,
      fetchPriority: "high"
    });
    preload(imagesLoader({
      src: takeover.topBannerTablet.imgSrc,
      width: takeover.topBannerTablet.width,
      quality: 75
    }), {
      as: "image",
      media: "(min-width: 600px) and (max-width: 1023px)",
      imageSrcSet: tabletSrcSet,
      fetchPriority: "high"
    });
    preload(imagesLoader({
      src: takeover.topBannerMobile.imgSrc,
      width: takeover.topBannerMobile.width,
      quality: 75
    }), {
      as: "image",
      media: "(max-width: 599px)",
      imageSrcSet: mobileSrcSet,
      fetchPriority: "high"
    });
    $[0] = imagesLoader;
    $[1] = takeover.topBannerDesktop;
    $[2] = takeover.topBannerMobile;
    $[3] = takeover.topBannerTablet;
    $[4] = desktopSrcSet;
    $[5] = mobileSrcSet;
    $[6] = tabletSrcSet;
  } else {
    desktopSrcSet = $[4];
    mobileSrcSet = $[5];
    tabletSrcSet = $[6];
  }
  const baseAlt = isLinked ? takeover.topBannerMobile.alt ?? "" : "";
  const aiLabelEnabled = takeover.topBannerMobile.aiLabel?.enabled === true;
  const isDisclosureInAlt = aiLabelEnabled && baseAlt !== "";
  let t1;
  if ($[7] !== baseAlt || $[8] !== formatAiMarkerMessage || $[9] !== isDisclosureInAlt) {
    t1 = isDisclosureInAlt ? `${baseAlt}, ${formatAiMarkerMessage("accessibleLabel")}` : baseAlt;
    $[7] = baseAlt;
    $[8] = formatAiMarkerMessage;
    $[9] = isDisclosureInAlt;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const displayAlt = t1;
  const aiMarkerAlt = isDisclosureInAlt ? baseAlt : "";
  let t2;
  if ($[11] !== aiLabelEnabled) {
    t2 = aiLabelEnabled ? {
      "data-digital-source-type": "compositeWithTrainedAlgorithmicMedia"
    } : {};
    $[11] = aiLabelEnabled;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const digitalSourceTypeAttr = t2;
  const showCloseButton = takeover.showCancel && onDismiss !== undefined;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[13] !== aiMarkerAlt || $[14] !== desktopSrcSet || $[15] !== digitalSourceTypeAttr || $[16] !== displayAlt || $[17] !== mobileSrcSet || $[18] !== showCloseButton || $[19] !== tabletSrcSet || $[20] !== takeover.link || $[21] !== takeover.topBannerDesktop || $[22] !== takeover.topBannerMobile || $[23] !== takeover.topBannerTablet) {
    const aiMarkers = [{
      key: "mobile",
      image: takeover.topBannerMobile,
      breakpointClassName: styles["banner__ai-marker--mobile"]
    }, {
      key: "tablet",
      image: takeover.topBannerTablet,
      breakpointClassName: styles["banner__ai-marker--tablet"]
    }, {
      key: "desktop",
      image: takeover.topBannerDesktop,
      breakpointClassName: styles["banner__ai-marker--desktop"]
    }].filter(_temp);
    let t7;
    if ($[28] !== desktopSrcSet || $[29] !== takeover.topBannerDesktop.height || $[30] !== takeover.topBannerDesktop.width) {
      t7 = <source media="(min-width: 1024px)" type="image/webp" srcSet={desktopSrcSet} sizes={bannerSizes} width={takeover.topBannerDesktop.width} height={takeover.topBannerDesktop.height} />;
      $[28] = desktopSrcSet;
      $[29] = takeover.topBannerDesktop.height;
      $[30] = takeover.topBannerDesktop.width;
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    let t8;
    if ($[32] !== tabletSrcSet || $[33] !== takeover.topBannerTablet.height || $[34] !== takeover.topBannerTablet.width) {
      t8 = <source media="(min-width: 600px) and (max-width: 1023px)" type="image/webp" srcSet={tabletSrcSet} sizes={bannerSizes} width={takeover.topBannerTablet.width} height={takeover.topBannerTablet.height} />;
      $[32] = tabletSrcSet;
      $[33] = takeover.topBannerTablet.height;
      $[34] = takeover.topBannerTablet.width;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    let t9;
    if ($[36] !== mobileSrcSet || $[37] !== takeover.topBannerMobile.height || $[38] !== takeover.topBannerMobile.width) {
      t9 = <source media="(max-width: 599px)" type="image/webp" srcSet={mobileSrcSet} sizes={bannerSizes} width={takeover.topBannerMobile.width} height={takeover.topBannerMobile.height} />;
      $[36] = mobileSrcSet;
      $[37] = takeover.topBannerMobile.height;
      $[38] = takeover.topBannerMobile.width;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    let t10;
    if ($[40] !== digitalSourceTypeAttr || $[41] !== displayAlt || $[42] !== takeover.topBannerMobile.height || $[43] !== takeover.topBannerMobile.imgSrc || $[44] !== takeover.topBannerMobile.mimeType || $[45] !== takeover.topBannerMobile.width) {
      t10 = <ImageComponent src={takeover.topBannerMobile.imgSrc} width={takeover.topBannerMobile.width} height={takeover.topBannerMobile.height} alt={displayAlt} mimeType={takeover.topBannerMobile.mimeType} className={styles.banner__image} priority={true} sizes={bannerSizes} {...digitalSourceTypeAttr} />;
      $[40] = digitalSourceTypeAttr;
      $[41] = displayAlt;
      $[42] = takeover.topBannerMobile.height;
      $[43] = takeover.topBannerMobile.imgSrc;
      $[44] = takeover.topBannerMobile.mimeType;
      $[45] = takeover.topBannerMobile.width;
      $[46] = t10;
    } else {
      t10 = $[46];
    }
    let t11;
    if ($[47] !== t10 || $[48] !== t7 || $[49] !== t8 || $[50] !== t9) {
      t11 = <picture>{t7}{t8}{t9}{t10}</picture>;
      $[47] = t10;
      $[48] = t7;
      $[49] = t8;
      $[50] = t9;
      $[51] = t11;
    } else {
      t11 = $[51];
    }
    const picture = t11;
    t3 = styles.banner;
    t4 = "takeover-top-banner";
    if ($[52] !== picture || $[53] !== takeover.link) {
      t5 = takeover.link === null ? picture : <LinkComponent link={takeover.link} className={styles.banner__link} dataTestId="takeover-banner-link">{picture}</LinkComponent>;
      $[52] = picture;
      $[53] = takeover.link;
      $[54] = t5;
    } else {
      t5 = $[54];
    }
    t6 = aiMarkers.map(t12 => {
      const {
        key,
        image: image_0,
        breakpointClassName
      } = t12;
      return <ImageAiMarkerComponent key={key} position={image_0.aiLabel?.position} style={image_0.aiLabel?.style} alt={aiMarkerAlt} className={classNames(breakpointClassName, getAiMarkerClassName(image_0.aiLabel?.position, showCloseButton))} />;
    });
    $[13] = aiMarkerAlt;
    $[14] = desktopSrcSet;
    $[15] = digitalSourceTypeAttr;
    $[16] = displayAlt;
    $[17] = mobileSrcSet;
    $[18] = showCloseButton;
    $[19] = tabletSrcSet;
    $[20] = takeover.link;
    $[21] = takeover.topBannerDesktop;
    $[22] = takeover.topBannerMobile;
    $[23] = takeover.topBannerTablet;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t3 = $[24];
    t4 = $[25];
    t5 = $[26];
    t6 = $[27];
  }
  let t7;
  if ($[55] !== formatMessage) {
    t7 = formatMessage("takeover.label.advertisement");
    $[55] = formatMessage;
    $[56] = t7;
  } else {
    t7 = $[56];
  }
  let t8;
  if ($[57] !== t7) {
    t8 = <span className={styles.banner__label}>{t7}</span>;
    $[57] = t7;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] !== formatMessage || $[60] !== onDismiss || $[61] !== showCloseButton) {
    t9 = showCloseButton && <button type="button" className={styles.banner__close} aria-label={formatMessage("takeover.label.close")} data-testid="takeover-close-button" onClick={e => {
      e.stopPropagation();
      onDismiss();
    }}>✕</button>;
    $[59] = formatMessage;
    $[60] = onDismiss;
    $[61] = showCloseButton;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  let t10;
  if ($[63] !== t3 || $[64] !== t4 || $[65] !== t5 || $[66] !== t6 || $[67] !== t8 || $[68] !== t9) {
    t10 = <div className={t3} data-testid={t4}>{t5}{t6}{t8}{t9}</div>;
    $[63] = t3;
    $[64] = t4;
    $[65] = t5;
    $[66] = t6;
    $[67] = t8;
    $[68] = t9;
    $[69] = t10;
  } else {
    t10 = $[69];
  }
  return t10;
}
function _temp(t0) {
  const {
    image
  } = t0;
  return image.aiLabel?.enabled === true;
}